import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Select, Button, Modal, Form, notification, Spin, Typography } from 'antd';
import { PlusOutlined, CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { listPatients, createPatient, loadPatientDto, getPatientDetails } from '../../actions/patientActions';
import { convertKeyToCamelCase, beautifyKey, formatDateOnly } from '../../utils/functions';
import { renderField } from '../crud/utils/renderUIComponents';
import { optionalIdentifierRule } from '../../utils/ukIdentifierValidation';
import ResponsiveFormLayout from '../crud/utils/ResponsiveFormLayout';
import { isPatientFormField } from '../../constants/patientFormFields';
import { buildQuickPatientCreatePayload, toDateOnlyIso } from './quickPatientCreatePayload';

const { Text } = Typography;
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const patientLabel = (patient) => {
  const name = `${patient.firstName || ''} ${patient.lastName || ''}`.trim() || patient.patientIdentifier;
  const dob = formatDateOnly(patient.dateOfBirth);
  return dob ? `${name} (${dob})` : name;
};

// Ported from promedhis: bespoke debounced searchable Select + Load More pagination,
// with an inline "Create New Patient" modal driven by ResponsiveFormLayout + renderField
// (not the full generic DynamicFormSP Lookup/Edit/View engine — this is a lightweight picker).
const QuickPatientSelect = ({ value, onChange, enableCreate = true, placeholder }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  // DateTime fields are not Ant Form fields, so picker values live here until submit.
  const draftRef = useRef({});

  const [patientListFilter, setPatientListFilter] = useState({ query: '', page: 1, limit: 10, filter: 'FirstName' });
  const [allPatients, setAllPatients] = useState([]);
  const [hasMorePatients, setHasMorePatients] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { loading: loadingList, patients = [], totalPages = 1, currentPage = 1 } = useSelector((state) => state.patientList || {});
  const { loading: loadingCreate, error: errorCreate, success: successCreate, patient: createdPatient } = useSelector((state) => state.patientCreate || {});
  const { loading: loadingDto, patientDto: dto } = useSelector((state) => state.patientDto || {});
  const { patient: selectedPatientDetails } = useSelector((state) => state.patientDetails || {});
  const userInfo = useSelector((state) => state.userLogin?.userInfo || {});

  useEffect(() => {
    dispatch(listPatients(patientListFilter));
  }, [dispatch, patientListFilter]);

  useEffect(() => {
    dispatch(loadPatientDto());
  }, [dispatch]);

  // Selected patient may be outside the currently loaded page(s) — fetch it directly so the label renders.
  useEffect(() => {
    if (value && value !== EMPTY_GUID && !allPatients.some((p) => p.id === value)) {
      dispatch(getPatientDetails(value));
    }
  }, [value, allPatients, dispatch]);

  useEffect(() => {
    if (selectedPatientDetails?.id && value === selectedPatientDetails.id && !allPatients.some((p) => p.id === selectedPatientDetails.id)) {
      setAllPatients((prev) => [selectedPatientDetails, ...prev]);
    }
  }, [selectedPatientDetails, value, allPatients]);

  useEffect(() => {
    if (patients.length > 0) {
      setAllPatients((prev) => (patientListFilter.page === 1 ? patients : [...prev, ...patients]));
      setHasMorePatients(currentPage < totalPages);
      setIsLoadingMore(false);
    }
  }, [patients, patientListFilter.page, currentPage, totalPages]);

  useEffect(() => {
    if (successCreate && createdPatient) {
      notification.info({
        message: t('Success'),
        description: t('Patient created successfully'),
        placement: 'bottom',
        icon: <CheckCircleTwoTone twoToneColor="#52c41a" />,
      });
      setIsModalVisible(false);
      draftRef.current = {};
      form.resetFields();
      setAllPatients((prev) => (prev.some((p) => p.id === createdPatient.id) ? prev : [createdPatient, ...prev]));
      onChange?.(createdPatient.id, {
        value: createdPatient.id,
        label: patientLabel(createdPatient),
        patientIdentifier: createdPatient.patientIdentifier || '',
      });
      dispatch({ type: 'PATIENT_CREATE_RESET' });
    }
  }, [successCreate, createdPatient]);

  useEffect(() => {
    if (errorCreate) {
      const message = Array.isArray(errorCreate) ? errorCreate.join(', ') : String(errorCreate);
      notification.info({
        message: t('Error'),
        description: `${t('Patient creation failed')}: ${message}`,
        placement: 'bottom',
        icon: <CloseCircleTwoTone twoToneColor="#eb2f96" />,
      });
    }
  }, [errorCreate]);

  const debouncedSearch = debounce((searchValue) => {
    setAllPatients([]);
    setPatientListFilter((prev) => ({ ...prev, query: searchValue || '', page: 1 }));
  }, 300);

  const loadMorePatients = () => {
    if (!loadingList && hasMorePatients && !isLoadingMore) {
      setIsLoadingMore(true);
      setPatientListFilter((prev) => ({ ...prev, page: prev.page + 1 }));
    }
  };

  const showModal = () => {
    setIsDropdownOpen(false);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    draftRef.current = {};
    form.resetFields();
  };

  const handleFieldChange = (field, val) => {
    const key = convertKeyToCamelCase(field);
    const stored = key === 'dateOfBirth' ? toDateOnlyIso(val) : val;
    draftRef.current = { ...draftRef.current, [key]: stored };
    form.setFieldsValue({ [key]: stored });
  };

  const handleCreate = (values) => {
    dispatch(createPatient(buildQuickPatientCreatePayload(values, draftRef.current)));
  };

  const dropdownRender = (menu) => (
    <div>
      {menu}
      {hasMorePatients && (
        <div style={{ padding: '4px 8px', borderTop: '1px solid #e8e8e8' }}>
          <Button type="link" size="small" loading={isLoadingMore} disabled={loadingList || isLoadingMore}
            onClick={loadMorePatients} style={{ width: '100%', textAlign: 'center', fontSize: 12 }}>
            {isLoadingMore ? t('Loading...') : t('Load More')}
          </Button>
        </div>
      )}
      {enableCreate && (
        <div style={{ padding: '4px 8px', borderTop: '1px solid #e8e8e8' }}>
          <Button type="link" size="small" icon={<PlusOutlined />} onClick={showModal}
            style={{ width: '100%', textAlign: 'left', fontSize: 12 }}>
            {t('Create New Patient')}
          </Button>
        </div>
      )}
    </div>
  );

  const displayValue = value === EMPTY_GUID ? undefined : value;

  return (
    <>
      <Select
        value={displayValue}
        onChange={(val) => {
          const selected = allPatients.find((p) => p.id === val);
          onChange?.(val, {
            value: val,
            label: selected ? patientLabel(selected) : '',
            patientIdentifier: selected?.patientIdentifier || '',
          });
        }}
        loading={loadingList}
        style={{ width: '100%' }}
        allowClear
        showSearch
        onSearch={debouncedSearch}
        placeholder={placeholder || `${t('Search')} ${t('Patient')}`}
        optionFilterProp="children"
        dropdownRender={dropdownRender}
        open={isDropdownOpen}
        onDropdownVisibleChange={setIsDropdownOpen}
        notFoundContent={loadingList ? <Spin size="small" /> : null}
      >
        {allPatients.length > 0 ? (
          allPatients.map((patient) => (
            <Select.Option key={patient.id} value={patient.id}>
              {patientLabel(patient)}
            </Select.Option>
          ))
        ) : (
          <Select.Option disabled>{t('No patients found')}</Select.Option>
        )}
      </Select>

      <Modal
        title={t('Create New Patient')}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
        destroyOnClose
      >
        {errorCreate && (
          <Text type="danger" style={{ display: 'block', marginBottom: 12 }}>
            {Array.isArray(errorCreate) ? errorCreate.join(', ') : errorCreate}
          </Text>
        )}
        {dto ? (
          <Form form={form} layout="vertical" onFinish={handleCreate} autoComplete="off">
            <ResponsiveFormLayout fieldsConfig={dto}>
              {Object.entries(dto).map(([fieldName, fieldProps]) => {
                if (!isPatientFormField(fieldName) || !fieldProps) return null;
                return (
                  <Form.Item
                    key={`form-item-${fieldName}`}
                    label={t(beautifyKey(fieldName))}
                    name={fieldProps.type !== 'DateTime' ? convertKeyToCamelCase(fieldName) : undefined}
                    rules={[
                      { required: fieldProps.isRequired, message: `${t(beautifyKey(fieldName))} ${t('is required')}` },
                      optionalIdentifierRule(fieldName),
                    ].filter(Boolean)}
                    style={{ marginBottom: 0 }}
                  >
                    {renderField(fieldName, fieldProps, form.getFieldsValue(), handleFieldChange, () => {}, userInfo)}
                  </Form.Item>
                );
              })}
            </ResponsiveFormLayout>
            <Form.Item style={{ marginTop: 16, marginBottom: 0, textAlign: 'right' }}>
              <Button onClick={handleCancel} style={{ marginRight: 8 }}>{t('Cancel')}</Button>
              <Button type="primary" htmlType="submit" loading={loadingCreate}>{t('Create Patient')}</Button>
            </Form.Item>
          </Form>
        ) : (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <Spin spinning={loadingDto} />
          </div>
        )}
      </Modal>
    </>
  );
};

export default QuickPatientSelect;
